import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState, type FormEvent } from 'react'
import { ApiError } from '../../api/client'
import {
  automatedUserPageQuery,
  automatedUserSettingsQuery,
  saveAutomatedUser,
} from '../../api/settingsEaseedeskAutomatedUser'
import { LoadFailed } from '../../ui/LoadFailed'
import { SelectPicker } from '../../ui/SelectPicker'
import { toast } from '../../ui/notify'
import { Spinner } from '../../ui/Spinner'

/**
 * Settings -> Easeedesk -> Automated User, element cu element fata de
 * `easeedesk_settings/automated_user/index.php` + `get_company_settings.php`:
 * selectorul de firma in stanga (cu "General for all companies from <furnizor>"
 * pe valoarea 0), formularul firmei alese in dreapta, adus la fiecare
 * schimbare (`index-1.js`), si Save care spune "Settings changed".
 */
export function AutomatedUserPage() {
  const page = useQuery(automatedUserPageQuery())
  const [company, setCompany] = useState('0')

  if (page.error instanceof ApiError && page.error.status === 403) {
    return (
      <div className="alert alert-danger">
        <i className="fas fa-exclamation-circle" aria-hidden="true"></i> You do not have access to this action.
      </div>
    )
  }
  if (page.error) {
    return <LoadFailed what="Automated User settings" error={page.error} onRetry={() => void page.refetch()} />
  }
  if (!page.data) return <Spinner />

  const options = [
    { value: '0', label: `General for all companies from ${page.data.providerName}`, subtext: 'General settings' },
    ...page.data.companies.map((c) => ({ value: String(c.ID), label: c.name })),
  ]

  return (
    <div>
      <h4>
        <strong>Automated User</strong> settings for <strong>{page.data.providerName} </strong>
      </h4>
      <label className="color" style={{ marginBottom: 0 }}>
        Manage settings for automated user settings{' '}
      </label>
      <hr />

      <section className="settings">
        <div className="row">
          <div className="col-md-5">
            <div className="form-group">
              <div className="input-group">
                <div className="input-group-addon  btn bg-success"> Select Company</div>
                <SelectPicker
                  id="automatedUserCompany"
                  name="company_settings"
                  className="form-control"
                  title="Select"
                  size={10}
                  options={options}
                  value={company}
                  onChange={setCompany}
                />
              </div>
            </div>
          </div>

          <div className="col-md-7">
            <div className="automated-company-settings">
              <CompanyForm key={company} company={Number(company)} />
            </div>
          </div>
        </div>
      </section>

      <hr />
    </div>
  )
}

function CompanyForm({ company }: { company: number }) {
  const client = useQueryClient()
  const settings = useQuery(automatedUserSettingsQuery(company))
  const [userName, setUserName] = useState('')

  useEffect(() => {
    if (settings.data) setUserName(settings.data.user_name)
  }, [settings.data])

  /* Formularul firmei se aduce la fiecare schimbare din casuta. Cand cererea
     lui pica, jumatatea dreapta ramanea o rotita care se invartea singura, si
     omul nu afla niciodata ca nu mai poate salva. */
  if (settings.error) {
    return <LoadFailed what="The settings for this company" error={settings.error} onRetry={() => void settings.refetch()} />
  }
  if (!settings.data) return <Spinner />

  const submit = (event: FormEvent) => {
    event.preventDefault()
    void saveAutomatedUser(company, userName)
      .then(() => {
        toast('Settings changed', 'Success')
        void client.invalidateQueries({ queryKey: ['settings', 'easeedesk', 'automated_user', company] })
      })
      .catch(() => toast('Something went wrong.', 'Error', 'error'))
  }

  return (
    <form className="automated_user_settings" onSubmit={submit}>
      <input type="hidden" name="company" value={company} />
      <div className="row">
        <div className="col-md-10 ">
          <div className="form-group">
            <div className="input-group">
              <div className="input-group-addon input-sm btn btn-default"> Automated User Name</div>
              <input
                type="text"
                name="user_name"
                className="form-control"
                value={userName}
                onChange={(event) => setUserName(event.target.value)}
              />
            </div>
          </div>
        </div>
        <div className="col-md-2 ">
          <button type="submit" className="btn btn-primary btn btn-success center-block update-automated-user-settings d-block">
            Save
          </button>
        </div>
      </div>
    </form>
  )
}
